import {
  batch,
  createSignal,
  createMemo,
  createEffect,
  onMount,
  onCleanup,
  Show,
  For,
} from "solid-js";
import DropDownButton from "../../../../../components/buttons/DropDownButton";
import Loading from "../../../../../components/utilities/Loading";
import FleetSourcesGate from "../../../../../components/utilities/FleetSourcesGate";
import FleetRankingTrendChart from "../../../../../components/charts/FleetRankingTrendChart";
import { persistantStore } from "../../../../../store/persistantStore";
import {
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  initializeDateRange,
  selectedSources,
} from "../../../../../store/filterStore";
import { apiEndpoints } from "@config/env";
import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad } from "../../../../../utils/logging";
import { debug as logDebug, error as logError } from "../../../../../utils/console";
import {
  createFleetSourcesReadyMemo,
  ensureFleetSourcesContextReady,
  resolveFleetSourceNamesForFetch,
} from "../../../../../utils/fleetSourcesReady";
import {
  FLEET_RANKING_CANCELLED_MESSAGE,
  MIN_FLEET_RANKING_SAMPLE_COUNT,
  fetchFleetRankingTrendAll,
  buildFleetRankingSeries,
  buildFleetRankingTwsBuckets,
  fleetRankingGroupByFromDropdown,
  fleetRankingDisplayFromDropdown,
  type FleetRankingMode,
  type FleetRankingGroupBy,
  type FleetRankingDisplayMode,
  type FleetRankingBucket,
  type FleetRankingTrendRow,
} from "../../../helpers/fleetRankingTrendApi";
import {
  fetchFleetRankingPrestartTrendAll,
  buildFleetRankingPrestartSeries,
  fleetRankingPrestartRankDirection,
  fleetRankingPrestartValueAxisLabel,
  fleetRankingPrestartValueTooltipLabel,
  fleetRankingPrestartValueAxisMax,
  fleetRankingPrestartValueAxisMin,
  type FleetRankingPrestartMetric,
  type FleetRankingPrestartRowsByMetric,
} from "../../../helpers/fleetRankingPrestartApi";
import { sourcesStore } from "../../../../../store/sourcesStore";

const { selectedClassName, selectedProjectId } = persistantStore;

const GROUP_OPTIONS = ["EVENT", "TWS BIN", "CONFIG"] as const;
const DISPLAY_OPTIONS = ["RANKING", "VALUE"] as const;

const PAGE_TITLE = "FLEET RANKING";
const PAGE_SUBTITLE =
  "Phase-average VMG% (grade > 1) from racing sessions. Straight-line sailing and accelerations combined. Maneuvers use all grades.";

/**
 * Samples a boat needs in a bucket before it is plotted or ranked. Applies to the phase and
 * maneuver panels in every view, including the hourly drill-down — an hour holds far more phases
 * than a single race, and a rank built on one maneuver is noise. Lower this if the gybe panel
 * reads too sparse. Prestart panels rank from a single start, so they pass 1 explicitly.
 */
const PHASE_MANEUVER_MIN_SAMPLES = MIN_FLEET_RANKING_SAMPLE_COUNT;

const FLEET_PHASE_PANELS: ReadonlyArray<{ mode: FleetRankingMode; title: string }> = [
  { mode: "upwind", title: "Straight line - Upwind" },
  { mode: "downwind", title: "Straight Line - Downwind" },
];

const FLEET_MANEUVER_PANELS: ReadonlyArray<{ mode: FleetRankingMode; title: string }> = [
  { mode: "tack", title: "Tacks" },
  { mode: "gybe", title: "Gybes" },
];

const FLEET_PRESTART_PANELS: ReadonlyArray<{ metric: FleetRankingPrestartMetric; title: string }> = [
  { metric: "prestart_dist", title: "Distance traveled to M1 in first 10 seconds" },
  { metric: "dtl_start", title: "Distance to Line at Start" },
];

type RowsByMode = Record<FleetRankingMode, FleetRankingTrendRow[]>;

function numericBucketOrder(row: { bucket_order: string | number | null; bucket_key: string }): number | null {
  const order = Number(row.bucket_order);
  if (Number.isFinite(order)) return order;

  const key = Number(row.bucket_key);
  return Number.isFinite(key) ? key : null;
}

const EMPTY_ROWS_BY_MODE = (): RowsByMode => ({
  upwind: [],
  downwind: [],
  tack: [],
  gybe: [],
});

const EMPTY_ROWS_BY_METRIC = (): FleetRankingPrestartRowsByMetric => ({
  prestart_dist: [],
  dtl_start: [],
});

export default function FleetRankingPage() {
  const [loading, setLoading] = createSignal(true);
  const [fetching, setFetching] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [rowsByMode, setRowsByMode] = createSignal<RowsByMode>(EMPTY_ROWS_BY_MODE());
  const [rowsByMetric, setRowsByMetric] = createSignal<FleetRankingPrestartRowsByMetric>(
    EMPTY_ROWS_BY_METRIC()
  );

  const [groupDropdown, setGroupDropdown] = createSignal<string>("EVENT");
  const [displayDropdown, setDisplayDropdown] = createSignal<string>("RANKING");
  const [selectedSourceNames, setSelectedSourceNames] = createSignal<string[]>([]);
  const [selectedEvent, setSelectedEvent] = createSignal<string | null>(null);

  const fleetSourcesReady = createFleetSourcesReadyMemo();

  /**
   * Normalized key for the global source selection so the load effect refetches on a real change
   * but not on a new array identity carrying the same names (cross-tab sync rebroadcasts).
   */
  const globalSourceKey = createMemo(() =>
    [...selectedSources()]
      .map((name) => String(name).trim().toLowerCase())
      .sort()
      .join("|")
  );

  const groupBy = createMemo((): FleetRankingGroupBy =>
    fleetRankingGroupByFromDropdown(groupDropdown())
  );
  /**
   * Drilling into an event averages by day, not by race. Race numbers are stamped by time rather
   * than by participation, so a race bucket can hold boats that never sailed it, and with a split
   * fleet each race only ever ranks 6-7 of the 12 boats. A day contains every flight, so the whole
   * fleet is ranked against itself.
   */
  const effectiveGroupBy = createMemo((): FleetRankingGroupBy => {
    if (groupBy() === "event" && selectedEvent()) {
      return "day";
    }
    return groupBy();
  });
  const displayMode = createMemo((): FleetRankingDisplayMode =>
    fleetRankingDisplayFromDropdown(displayDropdown())
  );

  const handleGroupSelection = (item: string): void => {
    setGroupDropdown(item);
    if (fleetRankingGroupByFromDropdown(item) !== "event") {
      setSelectedEvent(null);
    }
  };

  const handleBucketClick = (bucketKey: string): void => {
    if (groupBy() !== "event") return;
    if (selectedEvent()) {
      setSelectedEvent(null);
      return;
    }
    const normalized = bucketKey.trim();
    if (!normalized) return;
    setSelectedEvent(normalized);
  };

  const clearSelectedEvent = (): void => {
    setSelectedEvent(null);
  };

  const sharedTwsBuckets = createMemo((): FleetRankingBucket[] | undefined => {
    if (groupBy() !== "tws_bin") return undefined;

    const values = [
      ...Object.values(rowsByMode()).flat(),
      ...Object.values(rowsByMetric()).flat(),
    ]
      .map(numericBucketOrder)
      .filter((value): value is number => value != null && Number.isFinite(value));

    if (values.length === 0) return undefined;
    return buildFleetRankingTwsBuckets(Math.max(...values));
  });

  const phasePanelChartData = createMemo(() => {
    const rows = rowsByMode();
    const display = displayMode();
    const twsBuckets = sharedTwsBuckets();
    return FLEET_PHASE_PANELS.map((panel) => ({
      ...panel,
      chartData: buildFleetRankingSeries(
        rows[panel.mode] ?? [],
        display,
        twsBuckets,
        PHASE_MANEUVER_MIN_SAMPLES
      ),
    }));
  });

  const maneuverPanelChartData = createMemo(() => {
    const rows = rowsByMode();
    const display = displayMode();
    const twsBuckets = sharedTwsBuckets();
    return FLEET_MANEUVER_PANELS.map((panel) => ({
      ...panel,
      chartData: buildFleetRankingSeries(
        rows[panel.mode] ?? [],
        display,
        twsBuckets,
        PHASE_MANEUVER_MIN_SAMPLES
      ),
    }));
  });

  const prestartPanelChartData = createMemo(() => {
    const rows = rowsByMetric();
    const display = displayMode();
    const twsBuckets = sharedTwsBuckets();
    return FLEET_PRESTART_PANELS.map((panel) => {
      const rankDirection = fleetRankingPrestartRankDirection(panel.metric);
      return {
        ...panel,
        rankDirection,
        valueAxisLabel: fleetRankingPrestartValueAxisLabel(panel.metric),
        valueTooltipLabel: fleetRankingPrestartValueTooltipLabel(panel.metric),
        valueAxisMax: fleetRankingPrestartValueAxisMax(panel.metric),
        valueAxisMin: fleetRankingPrestartValueAxisMin(),
        chartData: buildFleetRankingPrestartSeries(
          rows[panel.metric] ?? [],
          display,
          rankDirection,
          effectiveGroupBy(),
          twsBuckets
        ),
      };
    });
  });

  const hasAnyChartData = createMemo(() =>
    phasePanelChartData().some((panel) => panel.chartData.buckets.length > 0) ||
    maneuverPanelChartData().some((panel) => panel.chartData.buckets.length > 0) ||
    prestartPanelChartData().some((panel) => panel.chartData.buckets.length > 0)
  );

  const legendSources = createMemo((): string[] => {
    const names = new Set<string>();
    for (const panel of [
      ...prestartPanelChartData(),
      ...phasePanelChartData(),
      ...maneuverPanelChartData(),
    ]) {
      for (const series of panel.chartData.series) {
        if (series.sourceName.trim()) {
          names.add(series.sourceName);
        }
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  });

  const toggleLegendSource = (sourceName: string): void => {
    const normalized = sourceName.trim();
    if (!normalized) return;
    setSelectedSourceNames((current) => {
      const lower = normalized.toLowerCase();
      const exists = current.some((name) => name.trim().toLowerCase() === lower);
      return exists
        ? current.filter((name) => name.trim().toLowerCase() !== lower)
        : [...current, normalized];
    });
  };

  const sourceLegendColor = (sourceName: string): string =>
    sourcesStore.getSourceColor(sourceName) || "#1f77b4";

  const isLegendSourceSelected = (sourceName: string): boolean => {
    const lower = sourceName.trim().toLowerCase();
    return selectedSourceNames().some((name) => name.trim().toLowerCase() === lower);
  };

  const isLegendSourceDimmed = (sourceName: string): boolean => {
    const selected = selectedSourceNames();
    if (selected.length === 0) return false;
    const lower = sourceName.trim().toLowerCase();
    return !selected.some((name) => name.trim().toLowerCase() === lower);
  };

  let fetchAbort: AbortController | undefined;
  /** Incremented per load; only the newest run may write signals. */
  let loadSeq = 0;

  const initializeDefaultDateRange = async (): Promise<void> => {
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (!className || !projectId) return;

      const result = await getData(
        `${apiEndpoints.app.datasets}/last_date?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&source_id=0`
      );
      if (!result.success || !result.data) return;

      const endDateValue = new Date(String(result.data).trim());
      if (Number.isNaN(endDateValue.getTime())) return;

      const startDateValue = new Date(endDateValue.getTime());
      startDateValue.setDate(startDateValue.getDate() - 365);

      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      // Batched so the load effect sees one update instead of firing on a mismatched date pair.
      batch(() => {
        setEndDate(formatDate(endDateValue));
        setStartDate(formatDate(startDateValue));
      });
    } catch (err) {
      logDebug("FleetRanking: initializeDefaultDateRange failed", err);
    }
  };

  const loadTrendData = async (): Promise<void> => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const start = startDate();
    const end = endDate();
    if (!className || !projectId || !start || !end) {
      setRowsByMode(EMPTY_ROWS_BY_MODE());
      setRowsByMetric(EMPTY_ROWS_BY_METRIC());
      setLoading(false);
      return;
    }

    fetchAbort?.abort();
    // Hold the controller locally: reading `fetchAbort` after an await can pick up a newer run's
    // controller, which would leave both runs uncancelled and let a stale response win.
    const controller = new AbortController();
    fetchAbort = controller;
    const runSeq = ++loadSeq;
    const isSuperseded = (): boolean => runSeq !== loadSeq || controller.signal.aborted;

    setFetching(true);
    setError(null);

    try {
      await ensureFleetSourcesContextReady({ className, projectId });
      if (isSuperseded()) return;

      const sourceNames = await resolveFleetSourceNamesForFetch({ className, projectId });
      if (isSuperseded()) return;

      const group = effectiveGroupBy();
      const eventFilter = selectedEvent() ?? undefined;

      const [trendResult, prestartResult] = await Promise.all([
        fetchFleetRankingTrendAll({
          className,
          projectId,
          startDate: start,
          endDate: end,
          groupBy: group,
          event: eventFilter,
          sourceNames,
          signal: controller.signal,
        }),
        fetchFleetRankingPrestartTrendAll({
          className,
          projectId,
          startDate: start,
          endDate: end,
          groupBy: group,
          event: eventFilter,
          sourceNames,
          signal: controller.signal,
        }),
      ]);

      if (isSuperseded()) return;

      if (
        (!trendResult.ok && trendResult.message === FLEET_RANKING_CANCELLED_MESSAGE) ||
        (!prestartResult.ok && prestartResult.message === FLEET_RANKING_CANCELLED_MESSAGE)
      ) {
        return;
      }

      if (!trendResult.ok) {
        setError(trendResult.message);
        setRowsByMode(EMPTY_ROWS_BY_MODE());
        setRowsByMetric(EMPTY_ROWS_BY_METRIC());
        return;
      }

      if (!prestartResult.ok) {
        setError(prestartResult.message);
        setRowsByMode(EMPTY_ROWS_BY_MODE());
        setRowsByMetric(EMPTY_ROWS_BY_METRIC());
        return;
      }

      batch(() => {
        setError(null);
        setRowsByMode(trendResult.rowsByMode);
        setRowsByMetric(prestartResult.rowsByMetric);
      });
    } catch (err) {
      if (isSuperseded()) return;
      logError("FleetRanking: loadTrendData failed", err);
      setError(err instanceof Error ? err.message : "Failed to load fleet ranking trend");
      setRowsByMode(EMPTY_ROWS_BY_MODE());
      setRowsByMetric(EMPTY_ROWS_BY_METRIC());
    } finally {
      // A superseded run must not clear the spinner while the newest run is still in flight.
      if (runSeq === loadSeq) {
        setFetching(false);
        setLoading(false);
      }
    }
  };

  onMount(async () => {
    void logPageLoad("FleetRanking.tsx", "Fleet Ranking", "Loaded");
    setupMediaContainerScaling();
    await initializeDateRange();
    if (!startDate() || !endDate()) {
      await initializeDefaultDateRange();
    }
  });

  onCleanup(() => {
    fetchAbort?.abort();
  });

  createEffect(() => {
    // effectiveGroupBy already depends on groupBy.
    effectiveGroupBy();
    selectedEvent();
    startDate();
    endDate();
    selectedClassName();
    selectedProjectId();
    // Tracked here (not inside loadTrendData, where it would sit behind an await and be untracked)
    // so a global source change refetches.
    globalSourceKey();
    const ready = fleetSourcesReady();
    // Wait for the sources catalog + selection hydration; FleetSourcesGate shows the spinner.
    if (!ready) return;
    void loadTrendData();
  });

  return (
    <div id="media-container" class="fleet-ranking-page cheat-sheet-page" data-fleet-ranking-page>
      <FleetSourcesGate message="Loading fleet sources...">
        <div class="container relative">
          <div class="performance-charts-scroll-container">
            <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner fleet-ranking-scroll-inner">
              <header class="fleet-ranking-header">
                <h1 class="report-page-title cheat-sheet-title">{PAGE_TITLE}</h1>
                <Show when={selectedEvent()}>
                  <nav class="fleet-ranking-breadcrumb" aria-label="Fleet ranking drill-down">
                    <span class="fleet-ranking-breadcrumb-root">FLEET RANKING</span>
                    <span class="fleet-ranking-breadcrumb-separator" aria-hidden="true">
                      /
                    </span>
                    <span class="fleet-ranking-breadcrumb-current">{selectedEvent()}</span>
                    <button
                      type="button"
                      class="fleet-ranking-breadcrumb-back"
                      onClick={clearSelectedEvent}
                    >
                      Back to events
                    </button>
                  </nav>
                </Show>
                <p class="fleet-ranking-data-scope">Race data only</p>
                <p class="fleet-ranking-subtitle">{PAGE_SUBTITLE}</p>
              </header>

              <div class="fleet-ranking-toolbar">
                <div class="fleet-ranking-controls">
                  <DropDownButton
                    options={[...GROUP_OPTIONS]}
                    defaultText={groupDropdown()}
                    handleSelection={handleGroupSelection}
                    size="medium"
                    smallLabel="Group by"
                  />
                  <DropDownButton
                    options={[...DISPLAY_OPTIONS]}
                    defaultText={displayDropdown()}
                    handleSelection={(item) => setDisplayDropdown(item)}
                    size="medium"
                    smallLabel="Display"
                  />
                </div>

                <Show when={legendSources().length > 0}>
                  <div class="fleet-ranking-legend" role="list" aria-label="Fleet sources">
                    <For each={legendSources()}>
                      {(sourceName) => (
                        <button
                          type="button"
                          class="fleet-ranking-legend-item"
                          classList={{
                            "is-selected": isLegendSourceSelected(sourceName),
                            "is-dimmed": isLegendSourceDimmed(sourceName),
                            "is-glow": ["NZL", "USA"].includes(sourceName.trim().toUpperCase()),
                          }}
                          aria-pressed={isLegendSourceSelected(sourceName)}
                          onClick={() => toggleLegendSource(sourceName)}
                        >
                          <span
                            class="fleet-ranking-legend-swatch"
                            style={{ "background-color": sourceLegendColor(sourceName) }}
                            aria-hidden="true"
                          />
                          <span class="fleet-ranking-legend-label">{sourceName.toUpperCase()}</span>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              <Show when={loading() || fetching()}>
                <Loading message={loading() ? "Loading fleet ranking..." : "Updating charts..."} />
              </Show>

              <Show when={!loading() && !fetching() && error()}>
                <p class="fleet-ranking-error" role="alert">
                  {error()}
                </p>
              </Show>

              <Show when={!loading() && !fetching() && !error() && !hasAnyChartData()}>
                <p class="fleet-ranking-empty">No fleet ranking data for the selected filters.</p>
              </Show>

              <Show when={!loading() && !fetching() && !error()}>
                <section class="fleet-ranking-prestart-section" aria-label="Prestart metrics">
                  <h2 class="fleet-ranking-section-title">Prestart</h2>
                  <Show when={displayMode() === "ranking" && prestartPanelChartData().some((panel) => panel.chartData.rankedFromStarts)}>
                    <p class="fleet-ranking-subtitle">
                      Boats are ranked inside each start, then those places are averaged. The line orders
                      teams by that average, from 1 to the number of teams in the event. 1 had the best
                      starts. Value is still the average metres.
                    </p>
                  </Show>
                  <div class="fleet-ranking-charts-grid fleet-ranking-prestart-grid">
                    <For each={prestartPanelChartData()}>
                      {(panel) => (
                        <section class="fleet-ranking-chart-panel">
                          <Show
                            when={panel.chartData.buckets.length > 0}
                            fallback={
                              <p class="fleet-ranking-empty fleet-ranking-chart-panel-empty">
                                No data for {panel.title}.
                              </p>
                            }
                          >
                            <FleetRankingTrendChart
                              buckets={panel.chartData.buckets}
                              series={panel.chartData.series}
                              displayMode={displayMode()}
                              groupBy={effectiveGroupBy()}
                              title={panel.title}
                              selectedSourceNames={selectedSourceNames()}
                              showLegend={false}
                              valueAxisLabel={panel.valueAxisLabel}
                              valueTooltipLabel={panel.valueTooltipLabel}
                              rankDirection={panel.rankDirection}
                              valueAxisAllowNegative={displayMode() === "value"}
                              valueAxisMax={displayMode() === "value" ? panel.valueAxisMax : undefined}
                              valueAxisMin={displayMode() === "value" ? panel.valueAxisMin : undefined}
                              minSampleCount={1}
                              onBucketClick={
                                groupBy() === "event" ? handleBucketClick : undefined
                              }
                            />
                          </Show>
                        </section>
                      )}
                    </For>
                  </div>
                </section>

                <section class="fleet-ranking-phase-section" aria-label="Phase VMG metrics">
                  <h2 class="fleet-ranking-section-title">Phases</h2>
                  <div class="fleet-ranking-charts-grid">
                    <For each={phasePanelChartData()}>
                      {(panel) => (
                        <section class="fleet-ranking-chart-panel">
                          <Show
                            when={panel.chartData.buckets.length > 0}
                            fallback={
                              <p class="fleet-ranking-empty fleet-ranking-chart-panel-empty">
                                No data for {panel.title}.
                              </p>
                            }
                          >
                            <FleetRankingTrendChart
                              buckets={panel.chartData.buckets}
                              series={panel.chartData.series}
                              displayMode={displayMode()}
                              groupBy={effectiveGroupBy()}
                              title={panel.title}
                              selectedSourceNames={selectedSourceNames()}
                              showLegend={false}
                              minSampleCount={PHASE_MANEUVER_MIN_SAMPLES}
                              onBucketClick={
                                groupBy() === "event" ? handleBucketClick : undefined
                              }
                            />
                          </Show>
                        </section>
                      )}
                    </For>
                  </div>
                </section>

                <section class="fleet-ranking-maneuver-section" aria-label="Maneuver VMG metrics">
                  <h2 class="fleet-ranking-section-title">Maneuvers</h2>
                  <div class="fleet-ranking-charts-grid">
                    <For each={maneuverPanelChartData()}>
                      {(panel) => (
                        <section class="fleet-ranking-chart-panel">
                          <Show
                            when={panel.chartData.buckets.length > 0}
                            fallback={
                              <p class="fleet-ranking-empty fleet-ranking-chart-panel-empty">
                                No data for {panel.title}.
                              </p>
                            }
                          >
                            <FleetRankingTrendChart
                              buckets={panel.chartData.buckets}
                              series={panel.chartData.series}
                              displayMode={displayMode()}
                              groupBy={effectiveGroupBy()}
                              title={panel.title}
                              selectedSourceNames={selectedSourceNames()}
                              showLegend={false}
                              minSampleCount={PHASE_MANEUVER_MIN_SAMPLES}
                              onBucketClick={
                                groupBy() === "event" ? handleBucketClick : undefined
                              }
                            />
                          </Show>
                        </section>
                      )}
                    </For>
                  </div>
                </section>
              </Show>
            </div>
          </div>
        </div>
      </FleetSourcesGate>
    </div>
  );
}
